/**
 * Persistent pause / play control for background video (WCAG 2.2.2, and 1.4.2 for any
 * clip that is not muted).
 *
 * The hero sections and the homepage custom-liquid block render <video autoplay loop>
 * with controls disabled, so moving content started on load with no way to stop it. This
 * attaches a real button to every autoplaying video on the page, and honours
 * prefers-reduced-motion by starting paused.
 *
 * Videos that opt out can set data-a11y-pause="skip".
 */
(function () {
  'use strict';

  var LABEL_PAUSE = 'Pause background video';
  var LABEL_PLAY = 'Play background video';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setState(video, button, playing) {
    // The swapping label carries the state. aria-pressed on top of it contradicted the
    // label ("Play ..., pressed" while stopped), so it is deliberately not used here.
    button.setAttribute('aria-label', playing ? LABEL_PAUSE : LABEL_PLAY);
    button.classList.toggle('is-paused', !playing);
    button.querySelector('.a11y-media-toggle__icon').textContent = playing ? '⏸' : '▶';
  }

  function decorate(video) {
    if (video.dataset.a11yPause === 'skip' || video.dataset.a11yPauseReady === 'true') return;
    // The homepage hero slider ships its own play/pause control - don't add a second one.
    if (video.closest && video.closest('.swiper')) return;
    video.setAttribute('data-a11y-pause-ready', 'true');

    var host = video.parentElement;
    if (!host) return;
    if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'a11y-media-toggle';
    button.innerHTML = '<span class="a11y-media-toggle__icon" aria-hidden="true">⏸</span>';

    button.addEventListener('click', function () {
      if (video.paused) {
        var played = video.play();
        if (played && typeof played.catch === 'function') played.catch(function () {});
      } else {
        video.pause();
      }
    });

    video.addEventListener('play', function () {
      setState(video, button, true);
    });
    video.addEventListener('pause', function () {
      setState(video, button, false);
    });

    host.appendChild(button);

    if (prefersReducedMotion()) {
      video.removeAttribute('autoplay');
      video.pause();
    }
    setState(video, button, !video.paused);
  }

  function scan() {
    var videos = document.querySelectorAll('video[autoplay]:not([data-a11y-pause-ready])');
    for (var i = 0; i < videos.length; i++) decorate(videos[i]);
  }

  var queued = false;
  function scanSoon() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Sections that build their markup from JS mount after DOMContentLoaded, so keep
  // watching for videos added later.
  if (window.MutationObserver) {
    new MutationObserver(scanSoon).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
